/**
 * 'harbor-edge' — the pier edge of 绿色码头 where 江子潮 writes the letter (20 × 12 tiles).
 * Authored as an ASCII sketch so the collision grid and layer data stay in sync.
 *
 *   W water   S moored ship hull (solid)   P pier planks   G gangway foot   B sand   C crate   T palm trunk
 *
 * The tileset id equals its texture key `tiles-harbor-edge` (WorldScene checks the layer tileset id as
 * a texture key); until the art lands the scene generates a procedural placeholder under that key
 * (maps/devTiles.ts).
 */
import type { MapData } from '@/core/types/contracts';
import { TILE } from '@/core/config';
import { PROLOGUE_SAILOR_KEY } from '../actors';

export const HARBOR_TILESET_TEXTURE = 'tiles-harbor-edge';
export const HARBOR_TILESET_ID = HARBOR_TILESET_TEXTURE;

export const HARBOR_EDGE_SKETCH = [
  'WWWWWWWWWWWWWWWWWWWW',
  'WWWWWWWWWWWSSSSSSSWW',
  'WWWWWWWWWWWSSSSSSSWW',
  'WWWWWWWWWWWSSSSSSSWW',
  'WWPPPPPPPPPPPGPPPPWW',
  'WWPPPPPPPPPPPPPPPPWW',
  'WWPPPPPPPPPPPPPPPPWW',
  'BBBBBBBBBBBBBBBBBBBB',
  'BBBBBBBBBBBBBBBBBBBB',
  'BBBBBBBBBCBBBBBBBBTB',
  'BBBBBBBBBBBBBBBBBBBB',
  'BBBBBBBBBBBBBBBBBBBB',
] as const;

/** Tile ids in the tileset: 0 empty, 1 water, 2 ship, 3 plank, 4 gangway, 5 sand, 6 crate, 7 palm. */
export const HARBOR_TILE: Record<string, number> = { W: 1, S: 2, P: 3, G: 4, B: 5, C: 6, T: 7 };
export const HARBOR_TILE_COUNT = 8;
const SOLID = new Set(['W', 'S', 'C', 'T']);

export function sketchToLayer(sketch: readonly string[]): { width: number; height: number; data: number[]; collision: number[] } {
  const height = sketch.length;
  const width = sketch[0]?.length ?? 0;
  const data: number[] = [];
  const collision: number[] = [];
  for (const row of sketch) {
    if (row.length !== width) throw new Error('ragged map sketch');
    for (const ch of row) {
      data.push(HARBOR_TILE[ch] ?? 0);
      collision.push(SOLID.has(ch) ? 1 : 0);
    }
  }
  return { width, height, data, collision };
}

const grid = sketchToLayer(HARBOR_EDGE_SKETCH);
const px = (tx: number, ty: number) => ({ x: tx * TILE + TILE / 2, y: ty * TILE + TILE - 4 });

export const HARBOR_EDGE_MAP: MapData = {
  id: 'harbor-edge',
  width: grid.width,
  height: grid.height,
  tileSize: 32,
  tilesets: [{ id: HARBOR_TILESET_ID, texture: HARBOR_TILESET_TEXTURE, tileWidth: 32, tileHeight: 32 }],
  layers: [{ name: 'ground', tileset: HARBOR_TILESET_ID, data: grid.data, depth: 'ground' }],
  collision: grid.collision,
  exits: [],
  spawns: [
    { id: 'letter-desk', ...px(5, 4), facing: 'up' },
    { id: 'gangway', ...px(13, 4), facing: 'down' },
    // 赵呈博 watches from the pier's east end: the map is 640×384 under a 640×360 view (camera scrollY ≤ 24),
    // so anything below world y ≈ 258 sits under the dialogue box. (17,6) keeps him on screen for his monologue.
    { id: 'zhao-watch', ...px(17, 6), facing: 'left' },
  ],
  interactions: [
    { id: 'harbor-edge-ship', x: 11 * TILE, y: 1 * TILE, w: 7 * TILE, h: 3 * TILE, kind: 'observe', dialogue: 'prologue-observe-ship', label: '看船' },
    { id: 'harbor-edge-water', x: 0, y: 0, w: 10 * TILE, h: 4 * TILE, kind: 'observe', dialogue: 'prologue-observe-water', label: '看海' },
  ],
  // Actor keys are ACTOR_DEFS keys (PROLOGUE_ACTORS[id].actorKey); ids are the scene-local handles.
  // The unloading sailors keep to the west / middle of the pier so 赵呈博's corner stays clear.
  npcs: [
    { id: 'zhao', actor: 'zhao-chengbo', ...px(17, 6), facing: 'left' },
    { id: 'returner', actor: 'traveller', ...px(14, 5), facing: 'left' },
    {
      id: 'sailor-1', actor: PROLOGUE_SAILOR_KEY, ...px(10, 6), facing: 'down',
      schedule: [
        { kind: 'walk', ...px(10, 5) }, { kind: 'wait', ms: 900 }, { kind: 'walk', ...px(8, 8) }, { kind: 'wait', ms: 1200 }, { kind: 'walk', ...px(10, 6) }, { kind: 'loop' },
      ],
    },
    {
      id: 'sailor-2', actor: PROLOGUE_SAILOR_KEY, ...px(13, 6), facing: 'left',
      schedule: [
        { kind: 'wait', ms: 1500 }, { kind: 'walk', ...px(12, 5) }, { kind: 'wait', ms: 700 }, { kind: 'walk', ...px(13, 8) }, { kind: 'face', facing: 'left' }, { kind: 'wait', ms: 1400 }, { kind: 'walk', ...px(13, 6) }, { kind: 'loop' },
      ],
    },
  ],
  water: [{ x: 0, y: 0, w: 20 * TILE, h: 4 * TILE }, { x: 0, y: 4 * TILE, w: 2 * TILE, h: 3 * TILE }, { x: 18 * TILE, y: 4 * TILE, w: 2 * TILE, h: 3 * TILE }],
  lights: [{ x: 13 * TILE, y: 2 * TILE, radius: 48, color: '#ffb347', intensity: 0.5, flicker: true }],
  music: 'bgm-prologue-letter',
  ambience: ['amb-harbor-water', 'amb-harbor-gulls'],
};
